import m from 'mithril'
import { initMithrilComponent } from '@client-shared/utils/mithril'
import IconButton from '@client-shared/components-v2/icon-button'
import Badge from '@client-shared/components-v2/badge'
import ProgressBar from '@client-shared/components-v2/progress-bar'
import IconFilePDF from '@client-shared/icons-v2/file-pdf'
import IconMinusCircle from '@client-shared/icons-v2/minus-circle'
import IconCheckmark from '@client-shared/icons-v2/checkmark'
import IconWarning from '@client-shared/icons-v2/warning'
import { formatFileSizeUnitForDisplay } from '@client-shared/utils/file-objects'
import type { UploadItem } from '../../util/uploads-manager'

import s from './uploads-row-item.module.scss'

interface Attrs extends m.Attributes {
	upload_item: UploadItem
	onCancelUpload?: (id: string) => void
}

const UploadsRowItem = initMithrilComponent<Attrs>()

UploadsRowItem.view = (vnode) => {
	const { upload_item, onCancelUpload } = vnode.attrs
	const {
		status = 'uploading',
		filename,
		uploaded_bytes,
		total_bytes,
		stats_message = '',
		error_message = '',
		post_processing_type = 'Post-processing',
		last_update,
		display_values,
	} = upload_item

	const progress_percent = Math.round((uploaded_bytes / total_bytes) * 100)
	const is_error = error_message.trim().length > 0

	let stats_text = ''
	let stats_more_text = ''

	const display_unit = formatFileSizeUnitForDisplay(display_values.size_unit)

	switch (status) {
		case 'uploading':
			stats_text = `${display_values.uploaded_size}/${display_values.total_size} ${display_unit}`

			if (stats_message.trim()) {
				stats_more_text = stats_message.trim()
			}

			break

		case 'canceled':
			stats_text = 'Canceled'
			break

		case 'complete':
			stats_text = `${display_values.total_size} ${display_unit}`

			if (last_update) {
				stats_more_text = 'Completed'
			}

			break

		case 'error':
		case 'post-processing-error':
			stats_text = `${display_values.uploaded_size}/${display_values.total_size} ${display_unit}`

			if (stats_message.trim()) {
				stats_more_text = error_message.trim()
			}

			break

		case 'post-processing':
			stats_text = post_processing_type

			if (stats_message.trim()) {
				stats_more_text = error_message.trim()
			}

			break

		default:
			break
	}

	return m(
		'div',
		{
			class: s.uploads_row_item,
			'data-state': status,
		},
		[
			m('div', { class: s.area_icon }, IconFilePDF),
			is_error && m('div', { class: s.area_badge }, m(Badge, { type: 'error' }, error_message.trim())),
			m('span', { class: s.area_name }, filename),
			m('div', { class: s.area_btns }, [
				status === 'error' && m('span', { class: s.icon_warning }, m(IconWarning)),
				status === 'uploading' &&
					m(IconButton, {
						class: s.btn_cancel,
						icon: IconMinusCircle,
						onClick: () => onCancelUpload?.(upload_item.id),
					}),
				status === 'complete' && m('span', { class: s.icon_checkmark }, m(IconCheckmark)),
			]),
			m('span', { class: s.area_stats }, [
				m('span', stats_text),
				stats_more_text.length > 0 && m('span', { class: s.stats_message }, stats_more_text),
			]),
			status === 'uploading' &&
				m('div', { class: s.area_progress }, m(ProgressBar, { progress_percentage: progress_percent })),
		],
	)
}

export default UploadsRowItem
